import { compact } from "lodash";
import pluralize from "pluralize";

import { TableRowErrorBoundary } from "components/error_boundary";
import { useArraySelector } from "hooks/redux_hooks";
import { BreakoutRoomType } from "shared/types";
import {
  WorkbookQuestionType,
  WorkbookQuestionTypeType,
  WorkbookType,
} from "shared/types/workbook";
import { workbookSelectors } from "store";

function WorkbookAnswersItem({
  breakoutRoom,
  workbook,
  questions,
}: {
  breakoutRoom?: BreakoutRoomType;
  workbook?: WorkbookType;
  questions: WorkbookQuestionType[];
}) {
  const filteredQuestions = questions.filter((question) =>
    question.components.some((component) => component.celltype !== "hidden"),
  );

  const getAnswerForDisplay = (question: WorkbookQuestionType) => {
    const answer = workbook?.answers[question.uuid];
    if (!answer) return "";
    const answerContent = Object.values(answer)[0];

    switch (question.type) {
      case WorkbookQuestionTypeType.Code:
      case WorkbookQuestionTypeType.LongForm:
        // Assumes the answer is stored in the first key
        return answerContent;

      case WorkbookQuestionTypeType.File: {
        const files = answerContent;
        if (!Array.isArray(files)) return "";
        return `${files.length} ${pluralize("file", files.length)} uploaded`;
      }

      case WorkbookQuestionTypeType.MultipleChoice: {
        const choices = answerContent;
        if (!Array.isArray(choices) || choices.length === 0 || typeof choices[0] !== "number") {
          return "";
        }
        return choices.map((choiceIndex: number) => (
          <div key={choiceIndex}>{question.multiple_choice_data[choiceIndex].label}</div>
        ));
      }

      case WorkbookQuestionTypeType.Table: {
        if (!Array.isArray(answerContent)) return "";
        const tableCells = answerContent.filter(Boolean);
        if (!tableCells.length) return "";
        return (
          <ul>
            {compact(tableCells).map((cell, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index} style={{ listStyle: "disc" }}>
                {cell}
              </li>
            ))}
          </ul>
        );
      }

      default:
        return "";
    }
  };

  const getColumnClass = () => {
    if (filteredQuestions.length <= 3) {
      return `col-${Math.floor(12 / filteredQuestions.length) - 1}`;
    }
    return "";
  };

  return (
    <tr style={{ borderBottom: "1px solid var(--black-tint-40)" }}>
      <td className="p4" style={{ width: "75px" }}>
        {breakoutRoom ? breakoutRoom.title : "(Shared)"}
      </td>
      {filteredQuestions.map((question) => (
        <td
          key={question.uuid}
          className={`p4 text-white ${getColumnClass()}`}
          style={{ maxWidth: "600px" }}
        >
          {getAnswerForDisplay(question)}
        </td>
      ))}
    </tr>
  );
}

function WorkbookAnswers({
  breakoutRooms,
  flavor,
  shareResourceBetweenGroups,
  hasOneResourceForAllFlavorsAndGroups = false,
}: {
  breakoutRooms: BreakoutRoomType[];
  flavor?: string;
  shareResourceBetweenGroups: boolean;
  hasOneResourceForAllFlavorsAndGroups?: boolean;
}) {
  const questions = useArraySelector(workbookSelectors.selectQuestionsByFlavor());
  const workbooks = useArraySelector(
    workbookSelectors.selectWorkbooksByFlavor(
      hasOneResourceForAllFlavorsAndGroups ? undefined : flavor,
    ),
  );

  if (!workbooks.length) return null;

  const questionUuids = Object.keys(workbooks[0].answers);
  const filteredQuestions = questions.filter((question) => questionUuids.includes(question.uuid));
  const nColumns = filteredQuestions.length + 1;

  return (
    <table className="w-full">
      <thead className="bg-black-tint-20 bold left-align">
        <tr>
          <th className="p4">Group</th>
          {filteredQuestions.map((question) => (
            <th key={question.uuid} className="p4" style={{ maxWidth: "250px" }}>
              <div className="truncate-five-lines">{question.question}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {!shareResourceBetweenGroups &&
          breakoutRooms.map((breakoutRoom) => (
            <TableRowErrorBoundary
              key={breakoutRoom.id}
              colSpan={nColumns}
              text="This item cannot be displayed"
              margin="4px"
            >
              <WorkbookAnswersItem
                breakoutRoom={breakoutRoom}
                workbook={workbooks.find(
                  (workbook) => workbook.breakout_room_id === breakoutRoom.id,
                )}
                questions={filteredQuestions}
              />
            </TableRowErrorBoundary>
          ))}

        {shareResourceBetweenGroups && (
          <TableRowErrorBoundary
            colSpan={nColumns}
            text="This item cannot be displayed"
            margin="4px"
          >
            <WorkbookAnswersItem workbook={workbooks[0]} questions={filteredQuestions} />
          </TableRowErrorBoundary>
        )}
      </tbody>
    </table>
  );
}

export { WorkbookAnswers, WorkbookAnswersItem };
